import { createContext, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Lets a page put its own controls into the shared top bar.
 *
 * Account Metrics needs this: its Simple/Advanced tablist is `Tablist - Detail
 * level` (1:9022), which sits in the top bar between Credentials and Public
 * Page rather than inside the page body.
 */
const TopbarSlotContext = createContext({ node: null, setNode: () => {} });

export function TopbarSlotProvider({ children }) {
  const [node, setNode] = useState(null);
  const value = useMemo(() => ({ node, setNode }), [node]);
  return <TopbarSlotContext.Provider value={value}>{children}</TopbarSlotContext.Provider>;
}

/** Read the current slot contents — used by Topbar. */
export function useTopbarSlotNode() {
  return useContext(TopbarSlotContext).node;
}

/**
 * Publish `render()` into the top bar for as long as this page is mounted.
 * `deps` behaves like useEffect's — list whatever the rendered node reads,
 * because passing JSX directly would re-publish on every render.
 */
export function useTopbarSlot(render, deps = []) {
  const { setNode } = useContext(TopbarSlotContext);

  useEffect(() => {
    setNode(render());
    return () => setNode(null);
  }, deps);
}
